const RELOAD_TIMESTAMP_KEY = 'chunk-recovery-reloaded-at'
const RELOAD_COOLDOWN_MS = 30 * 1000

/**
 * A cooldown rather than a one-shot flag: it still can't loop on a chunk that is genuinely
 * gone (the second failure inside the window falls through to the error page), but it also
 * doesn't permanently disarm itself, so a later deploy in the same tab recovers too.
 */
const canReload = (): boolean => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY))
    return !lastReloadAt || Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS
  } catch {
    // Storage can throw when cookies/site data are blocked; recovery is best-effort.
    return false
  }
}

/**
 * Recovers from a chunk that no longer exists. A deploy publishes new hashed filenames and
 * removes the old ones, so a tab left open across one cannot import any route it had not
 * already loaded — every page is its own chunk since route-level code splitting landed. Vite
 * reports that as `vite:preloadError`; without this the router's ErrorBoundary shows the error
 * page until the user reloads by hand. Reloading pulls the new manifest and lands them on the
 * page they actually asked for.
 */
export const registerChunkRecovery = (isSpeculativeFailure: () => boolean = () => false): void => {
  window.addEventListener('vite:preloadError', (event) => {
    // Left unhandled (not preventDefault): the import still rejects, so its caller can retry.
    if (isSpeculativeFailure() || !canReload()) {
      return
    }

    // Record the attempt BEFORE suppressing the error. Reading in canReload() can succeed
    // while writing throws (Safari private browsing raises QuotaExceededError on setItem),
    // and without the cooldown record a reload could loop. So if we can't persist, leave
    // Vite's default handling in place: the error page beats silently swallowing a failed
    // chunk load with no recovery.
    try {
      sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()))
    } catch {
      return
    }

    // Keep Vite from rethrowing — the reload below is the handling.
    event.preventDefault()
    window.location.reload()
  })
}
